import { EXCEEDANCE_RESOLUTION_TYPE_LABELS } from "encamp-shared/src/constants/generatorDiscrepancyDefaults";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { formatFieldValue } from "encamp-shared/src/waste/discrepancyValueFormat";
import { getFieldLabel } from "routes/Customer/Waste/Manifests/DiscrepancyReviewDialog.helpers";

/**
 * The shape of a manifest correction as both the drawer's linked list and the
 * mark-resolved picker consume it. Structural rather than the derived GraphQL
 * type so either surface can select a subset.
 */
export type ManifestCorrectionRow = {
  id: string;
  staffReported?: boolean;
  processedManifestId: string;
  manifestTrackingNumber: string;
  shippedDate?: string | null;
  /** Manifest line the corrected field is on; null for a manifest-level field. */
  wasteLineNumber?: string | null;
  field: string;
  /** Where the correction is in its own lifecycle. */
  status: string;
  /** Where the correction was applied; a path alone does not confirm completion. */
  resolutionPath?: string | null;
  eManifestValue?: string | null;
  vendorValue?: string | null;
  /** The value the reviewer declared correct; set on MANUAL_ENTRY rows. */
  correctValue?: string | null;
  resolvedAt?: string | null;
  resolvedByName?: string | null;
};

/**
 * Mirrors the server completion predicate: a terminal status and known corrected source.
 * Incomplete corrections keep the linked review pending.
 */
export function isCorrectionLanded(row: Pick<ManifestCorrectionRow, "status" | "resolutionPath">) {
  return (
    (row.status === "RESOLVED" || row.status === "IGNORED") && isLandedPath(row.resolutionPath)
  );
}

/**
 * The paths that say which side of the data moved. NEVER_IN_EMANIFEST is not one:
 * staff closed the row because no record will ever arrive.
 */
function isLandedPath(resolutionPath: string | null | undefined): boolean {
  return (
    resolutionPath === "EMANIFEST" ||
    resolutionPath === "VENDOR_DATA" ||
    resolutionPath === "MANUAL_ENTRY"
  );
}

/** A closed correction without a recorded fix cannot resolve a linked review. */
export function isCorrectionClosedUnattributed(
  row: Pick<ManifestCorrectionRow, "status" | "resolutionPath">
) {
  return (
    (row.status === "RESOLVED" || row.status === "IGNORED") && !isLandedPath(row.resolutionPath)
  );
}

/** Where a correction stands, as the chip text the picker and drawer table render. */
export function correctionStateLabel(
  row: Pick<ManifestCorrectionRow, "status" | "resolutionPath">
): string {
  if (isCorrectionLanded(row)) return "Corrected";
  if (isCorrectionClosedUnattributed(row)) return "Closed, corrected source unknown";
  if (row.status === "PENDING") return "In progress";
  return "Open";
}

/** Human label for the corrected discrepancy field. */
export function correctionFieldLabel(field: string): string {
  return getFieldLabel(field);
}

/**
 * The value a correction set the field to, or null while that isn't known yet.
 * - A reviewer-chosen correctValue wins: on a MANUAL_ENTRY row eManifestValue is the rejected value.
 * - Until the correction is complete, eManifestValue is still the wrong value.
 */
export function correctedToValue(
  row: Pick<
    ManifestCorrectionRow,
    "status" | "resolutionPath" | "eManifestValue" | "correctValue" | "staffReported"
  >
): string | null | undefined {
  const reviewerChose = row.staffReported || row.resolutionPath === "MANUAL_ENTRY";
  if (reviewerChose && row.correctValue != null) return row.correctValue;
  return isCorrectionLanded(row) ? row.eManifestValue : null;
}

/**
 * A corrected value for display. An empty string is a real outcome (the field was
 * cleared), so it reads as "(blank)" rather than an em dash that would look like
 * missing data.
 */
export function correctionValueLabel(field: string, value: string | null | undefined): string {
  if (value == null) return "—";
  return value.trim() ? formatFieldValue(field, value) : "(blank)";
}

/**
 * Which manifest line a correction is on, or "Manifest" for a manifest-level
 * field. Several lines on one manifest can carry the same field discrepancy, and
 * this is what tells those rows apart.
 */
export function correctionLineLabel(row: Pick<ManifestCorrectionRow, "wasteLineNumber">): string {
  return row.wasteLineNumber ? `Line ${row.wasteLineNumber}` : "Manifest";
}

/** Formats a day in the viewer's time zone; tests can supply an explicit zone. */
export function formatDay(value: string | null | undefined, timeZone?: string): string {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-US", timeZone ? { timeZone } : undefined);
}

// Widened so an unrecognized enum member falls through to prettyPrintEnumValue
// rather than failing to compile against the literal key union.
const RESOLUTION_TYPE_LABELS: Record<string, string> = EXCEEDANCE_RESOLUTION_TYPE_LABELS;

/** Display label for how an exceedance was resolved; null while it is still open. */
export function resolutionMethodLabel(resolutionType: string | null | undefined): string | null {
  if (!resolutionType) return null;
  return RESOLUTION_TYPE_LABELS[resolutionType] ?? prettyPrintEnumValue(resolutionType);
}

/**
 * Which side of the manifest was corrected. Both can account for an exceedance:
 * the calculated status is the max across sources, so the correction that
 * matters is the one on whichever source drove it.
 */
export function correctionPathLabel(resolutionPath: string | null | undefined): string {
  // Blank rather than an em dash: an in-flight correction has no corrected side
  // yet, which is not the same as a value we failed to find.
  if (!resolutionPath) return "";
  if (resolutionPath === "EMANIFEST") return "e-Manifest";
  if (resolutionPath === "VENDOR_DATA") return "Vendor data";
  if (resolutionPath === "MANUAL_ENTRY") return "Reviewer-chosen value";
  return prettyPrintEnumValue(resolutionPath);
}
